import { apiUtils } from './api';

// Simple feedback structure (what backend actually returns)
export interface SimpleFeedback {
  id: string;
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  rating: number;
  comments?: string;
  createdAt: string;
  updatedAt: string;
}

// Comprehensive demo feedback structure (for future comprehensive feedback)
export interface DemoFeedback {
  id: string;
  topic: string;
  otherTopicSpecify?: string;
  rating: number;
  sessionRating: string;
  interestMobile?: string;
  interestMasMigration?: string;
  interestSupportExt?: string;
  interestOther?: string;
  followUpMobile: boolean;
  followUpMasMigration: boolean;
  followUpSupportExt: boolean;
  followUpOther: boolean;
  followUpOtherSpecify?: string;
  timelineMobile?: string;
  timelineMasMigration?: string;
  timelineSupportExt?: string;
  approachMobile?: string;
  approachMasMigration?: string;
  approachSupportExt?: string;
  contactForDiscussion: boolean;
  additionalComments?: string;
  name: string;
  email: string;
  company?: string;
  sessionId: string;
  createdAt: string;
  updatedAt: string;
}

export interface DemoFeedbackListParams {
  page?: number;
  limit?: number;
  search?: string;
  topic?: string;
  rating?: number;
  contactForDiscussion?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface DemoFeedbackListResponse {
  feedbacks: SimpleFeedback[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DemoFeedbackStats {
  totalFeedbacks: number;
  averageRating: number;
  topicBreakdown: {
    topic: string;
    count: number;
    percentage: number;
  }[];
  ratingDistribution: {
    rating: number;
    count: number;
    percentage: number;
  }[];
  contactRequestRate: number;
  recentFeedbacks: number; // Last 7 days
}


export const demoFeedbackApi = {
  // Get list of feedback with filtering and pagination
  async getFeedbackList(params: DemoFeedbackListParams = {}): Promise<DemoFeedbackListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.topic) searchParams.append('topic', params.topic);
    if (params.rating) searchParams.append('rating', params.rating.toString());
    if (params.contactForDiscussion !== undefined) {
      searchParams.append('contactForDiscussion', params.contactForDiscussion.toString());
    }
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const response = await apiUtils.backend.get(`/demo/feedback?${searchParams.toString()}`);
    return response.data;
  },

  // Get feedback statistics
  async getFeedbackStats(): Promise<DemoFeedbackStats> {
    const response = await apiUtils.backend.get('/demo/feedback/stats');
    return response.data;
  },

  // Get single feedback by ID
  async getFeedbackById(id: string): Promise<DemoFeedback> {
    const response = await apiUtils.backend.get(`/demo/feedback/${id}`);
    return response.data;
  },

  // Delete feedback (for cleanup)
  async deleteFeedback(id: string): Promise<void> {
    await apiUtils.backend.delete(`/demo/feedback/${id}`);
  },

  // Export feedback data
  async exportFeedback(params: DemoFeedbackListParams = {}): Promise<Blob> {
    const searchParams = new URLSearchParams();
    
    if (params.search) searchParams.append('search', params.search);
    if (params.topic) searchParams.append('topic', params.topic);
    if (params.rating) searchParams.append('rating', params.rating.toString());
    if (params.contactForDiscussion !== undefined) {
      searchParams.append('contactForDiscussion', params.contactForDiscussion.toString());
    }
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const response = await apiUtils.backend.get(`/demo/feedback/export?${searchParams.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};